import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Empty, message } from 'antd';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import CentralParcelDetailDrawer from '../central/parcels/components/CentralParcelDetailDrawer';
import DamagedLabelLookupTable from './components/DamagedLabelLookupTable';
import {
    GENERATE_BATCH_SHIPPING_LABEL_PDF,
    GENERATE_SHIPPING_LABEL_PDF,
    GenerateBatchShippingLabelPdfResponse,
    GenerateBatchShippingLabelPdfVariables,
    GenerateShippingLabelPdfResponse,
    GenerateShippingLabelPdfVariables,
} from '../delivery/parcels/graphql/mutations/generateShippingLabelPdf';
import { getUserInfo } from '../../utils/auth';
import { showError } from '../../utils/exceptionUtils';
import { OrderType } from '../../graphql/types/OrderType';
import DamagedLabelLookupFilter from './components/DamagedLabelLookupFilter';
import { useDamagedLabelLookup } from './hooks/useDamagedLabelLookup';
import {
    DAMAGED_LABEL_LOOKUP_MAX_FIELD_LENGTH,
    DamagedLabelLookupInput,
    DamagedLabelLookupScope,
    EMPTY_DAMAGED_LABEL_LOOKUP_INPUT,
    validateDamagedLabelLookupInput,
} from './types/damagedLabelLookupTypes';
import {
    handlePrintLabelError,
    printShippingLabelsForOrders,
} from './utils/printShippingLabels';

/** 与后端 DAMAGED_PARCEL_MAX_PAGE_SIZE 一致；固定查第一页、最多 10 条，不做分页 */
const DAMAGED_LABEL_LOOKUP_PAGE_SIZE = 10;

function resolveScope(pathname: string): DamagedLabelLookupScope {
    return pathname.startsWith('/wms') ? 'wms' : 'central';
}

const DamagedLabelLookupPage: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const scope = useMemo(() => resolveScope(location.pathname), [location.pathname]);

    const [filterInput, setFilterInput] = useState<DamagedLabelLookupInput>(
        EMPTY_DAMAGED_LABEL_LOOKUP_INPUT
    );
    const [appliedFilters, setAppliedFilters] = useState<DamagedLabelLookupInput>(
        EMPTY_DAMAGED_LABEL_LOOKUP_INPUT
    );
    const [detailVisible, setDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<OrderType[]>([]);
    const [printLoading, setPrintLoading] = useState(false);

    const {
        parcelData,
        loading,
        error,
        totalElements,
        hasSearched,
        search,
        refetchSearch,
        resetResults,
    } = useDamagedLabelLookup(scope);

    const [generateShippingLabelPdf] = useMutation<
        GenerateShippingLabelPdfResponse,
        GenerateShippingLabelPdfVariables
    >(GENERATE_SHIPPING_LABEL_PDF);

    const [generateBatchShippingLabelPdf] = useMutation<
        GenerateBatchShippingLabelPdfResponse,
        GenerateBatchShippingLabelPdfVariables
    >(GENERATE_BATCH_SHIPPING_LABEL_PDF);

    const clearSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    }, []);

    const runSearch = useCallback(
        async (criteria: DamagedLabelLookupInput) => {
            const validationKey = validateDamagedLabelLookupInput(criteria);
            if (validationKey) {
                message.warning(
                    t(validationKey, { max: DAMAGED_LABEL_LOOKUP_MAX_FIELD_LENGTH })
                );
                return;
            }
            clearSelection();
            const result = await search(criteria, 1, DAMAGED_LABEL_LOOKUP_PAGE_SIZE);
            if (!result.ok) {
                showError(result.error);
            }
        },
        [clearSelection, search, t]
    );

    const handleSearch = () => {
        setAppliedFilters({ ...filterInput });
        void runSearch(filterInput);
    };

    const handleReset = () => {
        setFilterInput({ ...EMPTY_DAMAGED_LABEL_LOOKUP_INPUT });
        setAppliedFilters({ ...EMPTY_DAMAGED_LABEL_LOOKUP_INPUT });
        clearSelection();
        resetResults();
    };

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: OrderType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const showParcelDetail = (record: OrderType) => {
        setOrderId(record.id);
        setDetailVisible(true);
    };

    const handlePrintLabel = async () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOnePackage'));
            return;
        }

        setPrintLoading(true);
        try {
            await printShippingLabelsForOrders(
                selectedRows,
                generateShippingLabelPdf,
                generateBatchShippingLabelPdf,
                t
            );
        } catch (printError) {
            handlePrintLabelError(printError, t);
        } finally {
            setPrintLoading(false);
        }
    };

    useEffect(() => {
        clearSelection();
    }, [appliedFilters, clearSelection]);

    const showEmptyHint =
        hasSearched && !loading && !error && totalElements === 0;

    const showResultLimitHint =
        hasSearched && !loading && !error && totalElements >= DAMAGED_LABEL_LOOKUP_PAGE_SIZE;

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <h2 className="text-lg font-semibold py-3">{t('damagedLabelLookup.title')}</h2>

            <DamagedLabelLookupFilter
                value={filterInput}
                onChange={setFilterInput}
                onSearch={handleSearch}
                onReset={handleReset}
                onPrintLabel={() => void handlePrintLabel()}
                loading={loading}
                printLoading={printLoading}
            />

            {showEmptyHint && (
                <Alert
                    type="warning"
                    showIcon
                    className="mb-3"
                    message={t('damagedLabelLookup.empty.title')}
                    description={t('damagedLabelLookup.empty.description')}
                />
            )}

            <DamagedLabelLookupTable
                parcelData={parcelData}
                showParcelDetail={showParcelDetail}
                onSelectChange={handleSelectChange}
                selectedRowKeys={selectedRowKeys}
                loading={loading}
            />

            {showEmptyHint && (
                <Empty className="py-6" description={t('damagedLabelLookup.empty.short')} />
            )}

            {hasSearched && (
                <div className="text-gray-500 mt-2">
                    {t('table.pagination.totalItems', { total: totalElements })}
                    {showResultLimitHint && (
                        <span className="text-red-500">
                            （{t('damagedLabelLookup.resultLimitHint') as string}）
                        </span>
                    )}
                </div>
            )}

            <CentralParcelDetailDrawer
                visible={detailVisible}
                onClose={() => setDetailVisible(false)}
                orderId={orderId}
                userRole={getUserInfo()?.account?.role?.name || ''}
                onRefreshList={() => {
                    if (hasSearched) {
                        void refetchSearch(appliedFilters, 1, DAMAGED_LABEL_LOOKUP_PAGE_SIZE);
                    }
                }}
                showExternalTrack
            />
        </div>
    );
};

export default DamagedLabelLookupPage;
